/**
 * Single place that decides what happens when the browser discovers the
 * signed-in session is gone.
 *
 * Server functions guarded by `requireSupabaseAuth` throw a plain 401 text
 * Response when the bearer token is missing, malformed, or expired. Before
 * this module those rejections surfaced as raw error text on the page and the
 * user was stranded. Now every 401 (and every Supabase sign-out or failed
 * token refresh) funnels into one handler registered by `AuthProvider`, which
 * clears the session, clears the query cache, and sends the user to the
 * sign-in page with a redirect param.
 *
 * The handler is registered from React because it needs the router and the
 * query client. Everything else in the app just calls `handleAuthFailure()`
 * or `reportAuthFailure(error)`.
 */

/** True when the value looks like an expired or missing session rejection. */
export function isAuthFailure(error: unknown): boolean {
  if (error == null) return false;
  if (typeof Response !== "undefined" && error instanceof Response) {
    return error.status === 401;
  }
  const candidate = error as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
    message?: unknown;
  };
  if (candidate.status === 401) return true;
  if (candidate.statusCode === 401) return true;
  if (candidate.response?.status === 401) return true;
  const message =
    typeof candidate.message === "string" ? candidate.message : String(error);
  if (/unauthorized/i.test(message)) return true;
  return /\b401\b/.test(message);
}

/**
 * True when this browser currently holds (or recently held) a Supabase
 * session. A 401 without one is just a normal anonymous response, so the
 * expiry recovery must stay out of the way on public pages.
 */
export function hadLocalSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const storage = window.localStorage;
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key) continue;
      if (/^sb-.*-auth-token$/.test(key) && storage.getItem(key)) return true;
    }
  } catch {
    /* Storage can be blocked; fall through to the cached flag. */
  }
  return sessionKnown;
}

let sessionKnown = false;

/** Called by `AuthProvider` whenever the signed-in state changes. */
export function setSessionPresence(present: boolean): void {
  sessionKnown = present;
}

type Handler = () => void;

let handler: Handler | null = null;
let inFlight = false;

/** Registered once by `AuthProvider`. Returns an unregister function. */
export function registerAuthFailureHandler(fn: Handler): () => void {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

/**
 * Run the recovery once. Concurrent failures (a page firing several queries
 * at the same moment) collapse into a single sign-in navigation.
 */
export function handleAuthFailure(): void {
  if (inFlight) return;
  // Anonymous visitor: a 401 is the expected answer, not an expiry.
  if (!hadLocalSession()) return;
  if (!handler) return;
  inFlight = true;
  try {
    handler();
  } finally {
    // Release after the navigation settles so a later, genuinely new
    // expiry is still handled.
    setTimeout(() => {
      inFlight = false;
    }, 3000);
  }
}

/** Recover only when the given error is an auth failure. */
export function reportAuthFailure(error: unknown): boolean {
  if (!isAuthFailure(error)) return false;
  handleAuthFailure();
  return true;
}

let observerInstalled = false;

/**
 * Watch every browser request for a 401 coming back from a server function or
 * an edge function.
 *
 * A 401 raised inside a route loader never reaches the caller as a readable
 * error: the framework fails while deserializing the plain text body and the
 * page sees an unrelated TypeError. Observing the response status is the only
 * reliable signal, so it is done once, here, at the transport layer.
 */
export function installAuthFailureObserver(): () => void {
  if (typeof window === "undefined" || observerInstalled) return () => {};
  observerInstalled = true;
  const original = window.fetch.bind(window);
  const patched: typeof window.fetch = async (input, init) => {
    const response = await original(input, init);
    if (response.status === 401) {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      if (url.includes("/_serverFn/") || url.includes("/functions/v1/")) {
        handleAuthFailure();
      }
    }
    return response;
  };
  window.fetch = patched;
  return () => {
    if (window.fetch === patched) window.fetch = original;
    observerInstalled = false;
  };
}
